import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Accessible pricing-page tooltip.
 *
 * A dotted-underline button opens a positioned popover on hover, focus
 * or tap. Keyboard: Tab to focus, Enter/Space toggles, Escape closes.
 * Touch: tap toggles. Hover: opens after a short delay, closes on
 * mouseleave unless the trigger has keyboard focus.
 *
 * The popover carries `role="tooltip"` and the trigger carries
 * `aria-describedby`, so assistive tech announces the tip. The popover
 * ONLY mounts while open; the id is still deterministic from the
 * trigger so screen readers can resolve it when it becomes visible.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { mono, sans } from '../fonts.js';
export function Tooltip({ tip, theme, children, triggerStyle, layout = 'inline' }) {
    const id = useId();
    const tipId = `tip-${id}`;
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({
        left: 0,
        top: 0,
        side: 'below',
    });
    const btnRef = useRef(null);
    const tipRef = useRef(null);
    const hoverTimer = useRef(null);
    const place = useCallback(() => {
        const btn = btnRef.current;
        const pop = tipRef.current;
        if (btn === null || pop === null)
            return;
        const r = btn.getBoundingClientRect();
        const popW = pop.offsetWidth;
        const popH = pop.offsetHeight;
        const margin = 10;
        const vw = window.innerWidth;
        // prefer below; flip above when below overflows the viewport.
        let side = 'below';
        let top = r.bottom + margin;
        if (top + popH > window.innerHeight - 8) {
            side = 'above';
            top = r.top - popH - margin;
        }
        let left = r.left;
        if (left + popW > vw - 8)
            left = vw - popW - 8;
        if (left < 8)
            left = 8;
        setCoords({ left, top, side });
    }, []);
    useLayoutEffect(() => {
        if (!open)
            return undefined;
        place();
        const onScroll = () => place();
        const onResize = () => place();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [open, place]);
    useEffect(() => {
        if (!open)
            return undefined;
        const onKey = (ev) => {
            if (ev.key === 'Escape') {
                setOpen(false);
                btnRef.current?.focus();
            }
        };
        const onDocClick = (ev) => {
            const target = ev.target;
            if (target === null)
                return;
            if (btnRef.current !== null && btnRef.current.contains(target))
                return;
            if (tipRef.current !== null && tipRef.current.contains(target))
                return;
            setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDocClick);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDocClick);
        };
    }, [open]);
    function clearHoverTimer() {
        if (hoverTimer.current !== null) {
            window.clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
    }
    function scheduleHover(openIt) {
        clearHoverTimer();
        hoverTimer.current = window.setTimeout(() => setOpen(openIt), openIt ? 120 : 200);
    }
    const baseTrigger = {
        ...sans,
        border: 0,
        background: 'none',
        padding: 0,
        margin: 0,
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'help',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textDecorationColor: theme.dim,
        textUnderlineOffset: 4,
        textDecorationThickness: 1,
        display: layout === 'inline' ? 'inline' : 'block',
        width: layout === 'block' ? '100%' : undefined,
        ...triggerStyle,
    };
    return (_jsxs(_Fragment, { children: [_jsx("button", { ref: btnRef, type: "button", "aria-describedby": open ? tipId : undefined, "aria-expanded": open ? true : undefined, onClick: () => setOpen((v) => !v), onFocus: () => setOpen(true), onBlur: (ev) => {
                    const next = ev.relatedTarget;
                    if (next !== null && tipRef.current !== null && tipRef.current.contains(next))
                        return;
                    setOpen(false);
                }, onMouseEnter: () => scheduleHover(true), onMouseLeave: () => {
                    scheduleHover(false);
                }, style: baseTrigger, children: children }), open ? (_jsxs("span", { ref: tipRef, id: tipId, role: "tooltip", onMouseEnter: () => clearHoverTimer(), onMouseLeave: () => scheduleHover(false), style: {
                    ...sans,
                    position: 'fixed',
                    left: coords.left,
                    top: coords.top,
                    zIndex: 1000,
                    maxWidth: 'min(300px, calc(100vw - 32px))',
                    background: theme.text,
                    color: theme.bg,
                    border: `1px solid ${theme.text}`,
                    fontSize: 13,
                    lineHeight: 1.45,
                    padding: '9px 11px',
                    borderRadius: 3,
                    boxShadow: '0 6px 14px -8px rgba(0,0,0,.35)',
                    pointerEvents: 'auto',
                }, children: [tip, _jsx("span", { "aria-hidden": "true", style: {
                            ...mono,
                            position: 'absolute',
                            fontSize: 1,
                        } })] })) : null] }));
}
//# sourceMappingURL=Tooltip.js.map